import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useFeatureAccess } from '@/lib/featureAccess';
import { isFeatureEnabled } from '@/config/rollout';
import { FlaskConical, Package, ShoppingCart, FolderKanban, Microscope, DollarSign, LogOut, LayoutDashboard, Menu, X, CheckSquare, Settings, Dna, Rat, BarChart2 } from 'lucide-react';

export default function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const featureAccess = useFeatureAccess(user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [approvalCount, setApprovalCount] = useState(0);
  const approvalRequest = useRef(null);

  const loadApprovalCount = useCallback(async () => {
    if (!user || !['admin', 'manager'].includes(user.role)) {
      setApprovalCount(0);
      return;
    }
    if (approvalRequest.current) return approvalRequest.current.catch(() => {});
    const request = api.get('/procurement/approvals/count');
    approvalRequest.current = request;
    try {
      const res = await request;
      setApprovalCount(res.data.count || 0);
    } catch (error) {
      setApprovalCount(0);
    } finally {
      approvalRequest.current = null;
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(loadApprovalCount, 60000);
    return () => clearInterval(interval);
  }, [user, loadApprovalCount]);

  useEffect(() => {
    loadApprovalCount();
  }, [location.pathname, loadApprovalCount]);

  useEffect(() => {
    window.addEventListener('focus', loadApprovalCount);
    return () => window.removeEventListener('focus', loadApprovalCount);
  }, [loadApprovalCount]);

  const allNavigation = [
    { name: 'Buffers', href: '/buffers', icon: FlaskConical, roles: ['admin', 'manager', 'scientist', 'pm-readonly'] },
    { name: 'Dashboard', href: user?.role === 'scientist' ? '/dashboard' : '/', icon: LayoutDashboard, roles: ['scientist', 'manager','admin'], feature: 'dashboard' },
    { name: 'Approvals', href: '/approvals', icon: CheckSquare, roles: ['scientist','manager','admin'], feature: 'approvals' },
    { name: 'Protocols', href: '/protocols', icon: FlaskConical, roles: ['scientist', 'manager','admin'], feature: 'protocols' },
    { name: 'Inventory', href: '/inventory', icon: Package, roles: ['scientist', 'manager','admin'], feature: 'inventory' },
    { name: 'Cell Maintenance', href: '/cell-maintenance', icon: Dna, roles: ['scientist', 'manager','admin', 'pm-readonly'], feature: 'cellMaintenance' },
    { name: 'Mouse Records', href: '/mouse-records', icon: Rat, roles: ['manager','admin', 'pm-readonly'], feature: 'mouseRecords' },
    { name: 'Procurement', href: '/procurement', icon: ShoppingCart, roles: ['scientist', 'manager','admin'], feature: 'procurement' },
    { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['scientist','manager','admin'], feature: 'timeline' },
    { name: 'Protocol Runs', href: '/experiments', icon: Microscope, roles: ['scientist', 'manager','admin'], feature: 'experimentTracking' },
    { name: 'Feature access', href: '/feature-access', icon: Settings, roles: ['admin'], feature: 'experimentTracking' },
    { name: 'Data analysis', href: '/data-analysis', icon: BarChart2, roles: ['scientist', 'manager','admin'], feature: 'experimentTracking' },
    { name: 'Financials', href: '/financials', icon: DollarSign, roles: ['manager','admin'], feature: 'financials' },
    { name: 'Admin Console', href: '/admin', icon: Settings, roles: ['admin'] },
  ];

  // Filter navigation based on user role
  const navigation = allNavigation.filter(item => (
    item.roles.includes(user?.role) && (!item.feature || isFeatureEnabled(item.feature))
    && (item.href !== '/data-analysis' || featureAccess.allowed('facs_protocol_mapping_v2') || featureAccess.allowed('facs_unpooled_measurements_v1') || featureAccess.allowed('automatic_protocol_analysis_v1'))
  ));

  return (
    <div className="min-h-screen flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-background border-b border-border flex items-center justify-between px-4 z-30">
        <h1 className="text-lg font-semibold heading" data-testid="mobile-app-title">Lab Operations</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-button"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40" 
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 border-r border-border bg-background flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-border">
          <h1 className="text-xl font-semibold heading" data-testid="app-title">Lab Operations</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.name}</p>
          <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-link-${item.name.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium flex-1">{item.name}</span>
                {item.name === 'Approvals' && approvalCount > 0 && (
                  <span className="text-xs rounded-full bg-destructive text-destructive-foreground px-2 py-0.5" data-testid="approvals-badge">
                    🔔 {approvalCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onLogout}
            className="w-full justify-start"
            data-testid="logout-button"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pt-16 lg:pt-0" data-page-scroll>
        {children}
      </div>
    </div>
  );
}
